import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { SignUpForm } from '../components/Auth/SignUpForm.tsx';
import { useOAuth } from '../hooks/useOAuth.ts';
import type { LoginData } from '../types/index.ts';
import { apiService } from '../services/api.ts';
import { dashboardService } from '../services/dashboardService.ts';
import logoCorge from '../assets/logos/logo-corgee.png';
import { js } from '../config/env';

const config = js.settings;

function isValidEmail(email: string): boolean
{
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function resolveBrandImageUrl(path: string): string
{
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = String(js.settings.apiAuthURL || '').replace(/\/?$/, '/');
  return base + path.replace(/^\//, '');
}

const getRedirectPath = async (): Promise<string> =>
{
  // First login of a new tenant: password change and initial wizard come first
  if (localStorage.getItem('C2_changePasswordConfig'))
  {
    return '/UpdatePassword';
  }
  if (localStorage.getItem('C2_initialCustomize') === 'false')
  {
    return '/InitialWizard';
  }
  try
  {
    const betaInfo = await dashboardService.getIsBeta();
    localStorage.setItem('C2_isBeta', String(betaInfo.isBeta));
    return betaInfo.isBeta ? '/home' : '/Dashboards';
  }
  catch
  {
    return localStorage.getItem('C2_isBeta') === 'true' ? '/home' : '/Dashboards';
  }
};

export const LoginPage: React.FC = () =>
{
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { login, obtainExternalAccessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [oauthLoading, setOAuthLoading] = useState<boolean>(false);
  const [disabledAccount, setDisabledAccount] = useState<{ name: string } | null>(null);
  const [signUpSuccess, setSignUpSuccess] = useState<{ name: string; surname: string } | null>(
    null,
  );
  const [userNotFound, setUserNotFound] = useState<boolean>(false);
  const [forgotPassword, setForgotPassword] = useState<{
    open: boolean;
    email: string;
    loading: boolean;
    error: string;
    success: boolean;
  }>({ open: false, email: '', loading: false, error: '', success: false });

  useEffect(() =>
  {
    if (!forgotPassword.open)
    {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape')
      {
        setForgotPassword({ open: false, email: '', loading: false, error: '', success: false });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [forgotPassword.open]);

  const handleOAuthSuccess = useCallback(
    async (data: Record<string, string>) =>
    {
      if (data.userNotRegister === 'true')
      {
        setActiveTab('signup');
        setError('User not registered. Please complete the sign up.');
        setOAuthLoading(false);
        return;
      }

      if (data.two_factor_auth === 'true')
      {
        setError('Two-factor authentication is required.');
        setOAuthLoading(false);
        return;
      }

      if (data.provider_key && data.login_provider)
      {
        setOAuthLoading(true);

        try
        {
          const response = await obtainExternalAccessToken({
            provider: data.login_provider,
            userName: data.userName || '',
            externalAccessToken: data.provider_key,
          });

          if (response.access_token)
          {
            const path = await getRedirectPath();
            const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
            setTimeout(() =>
            {
              window.location.href = base + path;
            }, 500);
          }
          else
          {
            console.error('❌ No access_token received in OAuth response');
            setError('Authentication failed. Please try again.');
            setOAuthLoading(false);
          }
        }
        catch (err)
        {
          console.error('❌ Error obtaining OAuth token:', err);
          const axiosError = err as any;
          const errorData = axiosError.response?.data;
          if (errorData?.name)
          {
            setDisabledAccount({ name: errorData.name });
            setOAuthLoading(false);
            return;
          }
          setError('Authentication failed. Please try again.');
          setOAuthLoading(false);
        }
      }
      else if (data.error)
      {
        console.error('❌ OAuth returned error:', data.error, data.error_description);
        setError(data.error_description || data.error);
        setOAuthLoading(false);
      }
      else
      {
        console.error('❌ Invalid OAuth response. Data received:', data);
        setError('Authentication failed. Please try again.');
        setOAuthLoading(false);
      }
    },
    [obtainExternalAccessToken],
  );

  const handleOAuthError = useCallback((err: Error) =>
  {
    console.error('OAuth Error:', err);
    setError('Authentication failed. Please try again.');
    setOAuthLoading(false);
  }, []);

  const handleOAuthCancel = useCallback(() =>
  {
    setOAuthLoading(false);
  }, []);

  const { loginWithProvider } = useOAuth(
    config as any,
    tenant?.TenancyName || 'Default',
    activeTab === 'login' ? 1 : 2,
    handleOAuthSuccess,
    handleOAuthError,
    handleOAuthCancel,
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    if (name === 'email') setUserNotFound(false);
  }, []);

  const handleSubmit = useCallback(async () =>
  {
    if (!formData.email || !formData.password)
    {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError('');
    setUserNotFound(false);

    try
    {
      await login(formData.email, formData.password, tenant?.TenancyName);
    }
    catch (err: any)
    {
      // Show the reason returned by the API; fall back to the generic hint
      const data = err?.response?.data;
      const message =
        (typeof data === 'string' ? data : data?.error?.message) ||
        (err?.message !== 'Invalid credentials' ? err?.message : '');
      if (message)
      {
        setError(message);
      }
      else
      {
        setUserNotFound(true);
      }
      setLoading(false);
      return;
    }

    const path = await getRedirectPath();
    navigate(path, { replace: true });
    setLoading(false);
  }, [formData.email, formData.password, login, tenant?.TenancyName]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    {
      if (e.key === 'Enter')
      {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleGoogleLogin = useCallback(() =>
  {
    setOAuthLoading(true);
    loginWithProvider('Google');
  }, [loginWithProvider]);

  const handleOffice365Login = useCallback(() =>
  {
    setOAuthLoading(true);
    loginWithProvider('Microsoft');
  }, [loginWithProvider]);

  const handleForgotPasswordSubmit = useCallback(async () =>
  {
    const email = forgotPassword.email.trim();
    if (!email) return;

    setForgotPassword(prev => ({ ...prev, loading: true, error: '' }));

    try
    {
      const tenancyName = tenant?.TenancyName || 'Default';
      const params = new URLSearchParams({ email, tenantString: tenancyName });
      await apiService.post(`account/forgetPassword?${params.toString()}`);
      setForgotPassword(prev => ({ ...prev, loading: false, success: true }));
    }
    catch (err: any)
    {
      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        'An error occurred. Please try again.';
      setForgotPassword(prev => ({ ...prev, loading: false, error: message }));
    }
  }, [forgotPassword.email, tenant?.TenancyName]);

  return (
    <>
      {oauthLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#62accb]"></div>
              <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-[#62accb] opacity-20"></div>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Signing you in...</h3>
              <p className="text-gray-500 text-sm">Please wait while we process your login</p>
            </div>
            <div className="flex gap-1 mt-2">
              <div
                className="w-2 h-2 bg-[#62accb] rounded-full animate-bounce"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-[#62accb] rounded-full animate-bounce"
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className="w-2 h-2 bg-[#62accb] rounded-full animate-bounce"
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {forgotPassword.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/20"
            onClick={() =>
              setForgotPassword({
                open: false,
                email: '',
                loading: false,
                error: '',
                success: false,
              })
            }
          />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-sm p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-medium text-[#62accb]">Problems signing in?</h3>
              <button
                onClick={() =>
                  setForgotPassword({
                    open: false,
                    email: '',
                    loading: false,
                    error: '',
                    success: false,
                  })
                }
                className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4 -mt-0.5"
              >
                ×
              </button>
            </div>

            {forgotPassword.success ? (
              <>
                <p className="text-sm text-gray-600 mb-6">
                  We've sent you an email with instructions to reset your password.
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      setForgotPassword({
                        open: false,
                        email: '',
                        loading: false,
                        error: '',
                        success: false,
                      })
                    }
                    className="px-5 py-2 bg-[#62accb] hover:bg-[#4a9bd1] text-white text-sm rounded transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-5">
                  Enter your registered email and we'll send instructions to get you back to Corgee
                </p>
                <div className="mb-1">
                  <input
                    type="email"
                    placeholder="Email"
                    value={forgotPassword.email}
                    onChange={e =>
                      setForgotPassword(prev => ({ ...prev, email: e.target.value, error: '' }))
                    }
                    onKeyDown={e => e.key === 'Enter' && handleForgotPasswordSubmit()}
                    className="w-full border-0 border-b border-gray-300 focus:border-[#62accb] outline-none py-1.5 text-sm text-gray-700 placeholder-gray-400 bg-transparent transition-colors"
                    autoFocus
                  />
                </div>
                {(() =>
                {
                  const emailTrimmed = forgotPassword.email.trim();
                  const formatError =
                    emailTrimmed && !isValidEmail(emailTrimmed)
                      ? 'A valid e-mail is required.'
                      : '';
                  const displayError = formatError || forgotPassword.error;
                  return displayError ? (
                    <p className="text-xs text-red-500 mt-2">{displayError}</p>
                  ) : null;
                })()}
                <div className="flex justify-end mt-5">
                  <button
                    onClick={handleForgotPasswordSubmit}
                    disabled={(() =>
                    {
                      const emailTrimmed = forgotPassword.email.trim();
                      return forgotPassword.loading || !emailTrimmed || !isValidEmail(emailTrimmed);
                    })()}
                    className="px-5 py-2 bg-[#62accb] hover:bg-[#4a9bd1] text-white text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgotPassword.loading ? 'Sending...' : 'Continue'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[#ededed] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative pt-20 mt-8">
          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2">
            {tenant?.CustomBrandingEnabled && tenant?.ImageBrand ? (
              <div className="w-36 h-36 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-white overflow-hidden">
                <img
                  src={resolveBrandImageUrl(tenant.ImageBrand)}
                  alt={tenant.BrandName || 'Brand Logo'}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-36 h-36 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                <img src={logoCorge} alt="Corgee Logo" className="w-28 h-28 object-contain" />
              </div>
            )}
          </div>

          {disabledAccount ? (
            <div className="p-8 pb-6 text-center">
              <h2 className="text-2xl text-[#62accb] font-normal mb-1">
                Hello {disabledAccount.name}!
              </h2>
              <p className="text-xl text-[#62accb] font-normal mb-4">Your account is disabled.</p>
              <hr className="border-gray-200 mb-4" />
              <p className="text-sm text-gray-600 mb-6">
                You will receive an email when the administrator approves the reactivation account
                request.
              </p>
              <button
                onClick={() => setDisabledAccount(null)}
                className="text-sm text-[#62accb] hover:text-[#4a9bd1] transition-colors"
              >
                Back to Corgee
              </button>
              <div className="mt-6 text-center text-xs text-gray-400">
                Corgee © CGBOT {new Date().getFullYear()}
              </div>
            </div>
          ) : signUpSuccess ? (
            <div className="p-8 pb-6 text-center">
              <h2 className="text-2xl text-[#62accb] font-normal mb-1">
                Hello {signUpSuccess.name} {signUpSuccess.surname}!
              </h2>
              <p className="text-xl text-[#62accb] font-normal mb-4">
                Your login request has been sent.
              </p>
              <hr className="border-gray-200 mb-4" />
              <p className="text-sm text-gray-600 mb-6">
                You will receive an email when the administrator approves the account request.
              </p>
              <button
                onClick={() =>
                {
                  setSignUpSuccess(null);
                  setActiveTab('login');
                }}
                className="text-sm text-[#62accb] hover:text-[#4a9bd1] transition-colors"
              >
                Back to Corgee
              </button>
              <div className="mt-6 text-center text-xs text-gray-400">
                Corgee © CGBOT {new Date().getFullYear()}
              </div>
            </div>
          ) : (
            <>
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('login')}
                  className={`flex-1 py-3 text-center font-medium transition-colors ${
                    activeTab === 'login'
                      ? 'text-[#62accb] border-b-2 border-[#62accb]'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Log in
                </button>
                <button
                  onClick={() => setActiveTab('signup')}
                  className={`flex-1 py-3 text-center font-medium transition-colors ${
                    activeTab === 'signup'
                      ? 'text-[#62accb] border-b-2 border-[#62accb]'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              <div className="p-4 pb-6">
                {tenant && (
                  <h2 className="text-2xl text-center text-[#62accb] mb-2 font-normal">
                    Welcome to{' '}
                    {tenant.CustomBrandingEnabled && tenant.BrandName ? tenant.BrandName : 'Corgee'}
                  </h2>
                )}
                {activeTab === 'login' ? (
                  <>
                    <p className="text-center text-gray-500 text-sm mb-4 font-light">
                      Log in with:
                    </p>

                    <div className="flex gap-4 mb-5">
                      <button
                        onClick={handleGoogleLogin}
                        disabled={oauthLoading || loading}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all text-gray-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        <span className="text-sm font-medium">Google</span>
                      </button>

                      <button
                        onClick={handleOffice365Login}
                        disabled={oauthLoading || loading}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all text-gray-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#ea3e23" d="M30.7,49,5,39.51,30.7,43V8.45l-17,4.12V36.07L5,39.45V10.66L30.7,1,45,5.07V44.93Z" />
                        </svg>
                        <span className="text-sm font-medium">Office 365</span>
                      </button>
                    </div>

                    <p className="text-center text-gray-500 text-sm mb-4 font-light">
                      Or enter your account info:
                    </p>

                    {userNotFound && (
                      <p
                        style={{
                          margin: '12px 0',
                          padding: '4px 12px',
                          backgroundColor: '#ffe6e6',
                          color: 'black',
                          fontSize: '12px',
                          textAlign: 'center',
                        }}
                      >
                        The user does not exist, Do you want to add a new user?
                      </p>
                    )}

                    <div>
                      {error && (
                        <p
                          style={{
                            margin: '12px 0',
                            padding: '4px 12px',
                            backgroundColor: '#ffe6e6',
                            color: 'black',
                            fontSize: '12px',
                            textAlign: 'center',
                          }}
                        >
                          {error}
                        </p>
                      )}

                      <div className="mb-5">
                        <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 focus-within:border-[#62accb] focus-within:ring-1 focus-within:ring-[#62accb] transition-all bg-white">
                          <Mail className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                          <input
                            type="email"
                            name="email"
                            autoComplete="username"
                            placeholder="Username/Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 focus-within:border-[#62accb] focus-within:ring-1 focus-within:ring-[#62accb] transition-all bg-white">
                          <Lock className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                          <input
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
                          />
                        </div>
                      </div>

                      <div className="text-center mb-6">
                        <button
                          onClick={() =>
                            setForgotPassword({
                              open: true,
                              email: '',
                              loading: false,
                              error: '',
                              success: false,
                            })
                          }
                          className="text-sm text-[#62accb] hover:text-[#4a9bd1] transition-colors"
                        >
                          Forgot Password?
                        </button>
                      </div>

                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full bg-[#62accb] hover:bg-[#4a9bd1] text-white py-3.5 rounded-lg font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                      >
                        {loading ? 'Logging in...' : 'Log in'}
                      </button>
                    </div>
                  </>
                ) : (
                  <SignUpForm
                    onGoogleLogin={handleGoogleLogin}
                    onOffice365Login={handleOffice365Login}
                    onSuccess={(name, surname) => setSignUpSuccess({ name, surname })}
                  />
                )}

                <div className="mt-4 text-center text-xs text-gray-400">
                  Corgee © CGBOT {new Date().getFullYear()}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
